import type { QueryObject } from '@/shared/lib/serializeQuery';
import type { SearchCriteriaParams, TouristGroupRoom } from '@/shared/model';
import { toAges, toCount, toValueList } from './values';
import { DEFAULT_INFANT_AGE, DEFAULT_KID_AGE } from '../../config/tourists';

type TouristGroup = SearchCriteriaParams['touristGroup'];

function resolveAges(ages: number[], kids: number, infants: number): number[] {
  if (ages.length || !(kids + infants)) {
    return ages;
  }

  return [
    ...Array.from({ length: infants }, () => DEFAULT_INFANT_AGE),
    ...Array.from({ length: kids }, () => DEFAULT_KID_AGE),
  ];
}

function toRoom(room: QueryObject): TouristGroupRoom {
  const kids = toCount(room.kids);
  const infants = toCount(room.infants);

  return {
    adults: toCount(room.adults),
    kids,
    infants,
    ages: resolveAges(toAges(room.ages), kids, infants),
  };
}

function toRoomList(rooms: unknown): QueryObject[] {
  return toValueList(rooms).filter((room): room is QueryObject => Boolean(room) && typeof room === 'object');
}

export function touristGroup(query: QueryObject): Pick<SearchCriteriaParams, 'touristGroup'> | null {
  if (toCount(query.adults)) {
    return { touristGroup: toRoom(query) };
  }

  const rooms = toRoomList(query.rooms).map((room) => toRoom(room));
  if (!rooms.length || !rooms[0].adults) {
    return null;
  }

  const [first, ...rest] = rooms;
  const group: TouristGroup = first;

  if (rest.length) {
    group.touristsByRooms = rest;
  }

  return { touristGroup: group };
}
